// Карточка активности: показатели, зоны пульса, график пульса, привязка к тренировке.

import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, Select, useToast } from '@/components/ui'
import { formatDayTitle, formatTime } from '@/lib/format'

import { useActivity, useDaySessions, useLinkActivity, type ActivityDetail } from './api'
import { activityTitle, sportLabel } from './logic'
import { ActivityStats, HrChart, ZoneBars } from './parts'

function LinkCard({ activity: a }: { activity: ActivityDetail }) {
  const sessions = useDaySessions(a.day)
  const link = useLinkActivity()
  const toast = useToast()
  const [choice, setChoice] = useState('')
  const options = (Array.isArray(sessions.data?.items) ? sessions.data.items : [])
    .filter((s) => s.id !== a.workout_session_id)
    .map((s) => ({
      value: s.id,
      label: `${formatTime(s.started_at)}${s.ended_at ? `–${formatTime(s.ended_at)}` : ''} · ${s.template_name ?? 'без шаблона'}`,
    }))
  const save = (sessionId: string | null) =>
    link.mutate(
      { id: a.id, sessionId },
      {
        onSuccess: () => {
          setChoice('')
          toast.show({ message: sessionId ? 'Активность привязана к тренировке' : 'Активность отвязана' })
        },
      },
    )

  return (
    <Card
      title="Тренировка"
      subtitle={a.link_manual ? 'Привязка выбрана вручную — синхронизация её не меняет.' : 'Привязывается сама, если время пересекается (±15 минут).'}
    >
      {a.session ? (
        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
          <Link to={`/workouts/${a.session.id}`} className="font-medium text-accent hover:underline">
            {a.session.template_name ?? 'Тренировка'} · {formatTime(a.session.started_at)}
          </Link>
          {a.link_manual ? <Badge>вручную</Badge> : <Badge tone="accent">автоматически</Badge>}
          <Button size="sm" variant="ghost" loading={link.isPending && choice === ''} onClick={() => save(null)}>
            Отвязать
          </Button>
        </div>
      ) : (
        <p className="mb-3 text-sm text-muted">Не привязана к тренировке.</p>
      )}
      {options.length > 0 ? (
        <div className="flex flex-wrap items-end gap-2">
          <Select
            label="Привязать к тренировке этого дня"
            value={choice}
            placeholder="— выбери тренировку —"
            options={options}
            onChange={(e) => setChoice(e.target.value)}
            containerClassName="min-w-[16rem]"
          />
          <Button disabled={!choice} loading={link.isPending && choice !== ''} onClick={() => save(choice)}>
            Привязать
          </Button>
        </div>
      ) : null}
      <ErrorText error={link.error ?? sessions.error} className="mt-2" />
    </Card>
  )
}

export function ActivityPage() {
  const { id = '' } = useParams()
  const activity = useActivity(id)
  const back = { to: '/activities', label: 'Активности' }

  if (activity.isPending) return <PageSpinner />
  if (activity.error) {
    return (
      <>
        <PageHeader title="Активность" back={back} />
        <Card>
          <EmptyState icon="alert" title="Не удалось открыть активность" description={<ErrorText error={activity.error} />} />
        </Card>
      </>
    )
  }
  const a = activity.data
  const day = formatDayTitle(a.start_at)
  return (
    <>
      <PageHeader
        title={activityTitle(a)}
        back={back}
        description={`${day.charAt(0).toUpperCase()}${day.slice(1)} · ${formatTime(a.start_at)} · ${sportLabel(a.sport)}`}
      />
      <Card>
        <ActivityStats activity={a} />
      </Card>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="Пульс по времени">
          <HrChart series={a.hr_series} />
        </Card>
        <Card title="Пульсовые зоны">
          <ZoneBars zones={a.hr_zones_s} />
        </Card>
      </div>
      <div className="mt-4">
        <LinkCard activity={a} />
      </div>
    </>
  )
}
